// Sticky bottom CTA bar — appears after scroll, dismissable.
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { useLang } from "../contexts/LangContext";

import { useFooterVisible } from "../hooks/useFooterVisible";

const LS_KEY = "talkani_sticky_cta_dismissed_v1";
// Hide on these routes — booking flow doesn't need a "book now" CTA;
// legal pages must never have their links covered.
const HIDDEN_ROUTES = [
  "/book", "/booking", "/admin", "/login", "/dashboard",
  "/forgot-password", "/reset-password",
  "/impressum", "/datenschutz", "/agb",
];

export default function StickyCTABar() {
  const { t } = useLang();
  const ct = t.sticky_cta;
  const location = useLocation();
  const footerVisible = useFooterVisible();

  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(LS_KEY) === "1");

  const isHidden = HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r));

  useEffect(() => {
    if (dismissed || isHidden) return;
    const onScroll = () => {
      // Show after user scrolls past 800px (i.e., past the hero)
      setVisible(window.scrollY > 800);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed, isHidden]);

  if (dismissed || isHidden || !visible || footerVisible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 bg-[#090A0B] text-white shadow-2xl border-t-2 border-[#FF5A00] animate-[slideUp_.5s_cubic-bezier(.16,1,.3,1)] hidden sm:block"
      data-testid="sticky-cta-bar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3.5 flex items-center gap-4">
        <div className="flex-1 flex items-center gap-3 min-w-0">
          <span className="hidden md:flex items-center justify-center w-9 h-9 rounded-full bg-[#FF5A00]/15 text-[#FF5A00] flex-shrink-0">
            <ArrowRight size={16} />
          </span>
          <div className="min-w-0">
            <div className="font-black tracking-tight text-base truncate">{ct.title}</div>
            <div className="text-[11px] text-neutral-400 tracking-wide truncate">{ct.subtitle}</div>
          </div>
        </div>
        <Link
          to="/book"
          className="flex-shrink-0 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold tracking-wider uppercase text-xs px-5 py-3 inline-flex items-center gap-2 transition-colors"
          data-testid="sticky-cta-book-btn"
        >
          {ct.book_now}
          <ArrowRight size={14} />
        </Link>
        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            sessionStorage.setItem(LS_KEY, "1");
          }}
          aria-label="Close"
          className="flex-shrink-0 text-neutral-500 hover:text-white transition p-1"
          data-testid="sticky-cta-close"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
